<template>
  <div class="training-history">
    <div class="training-history__header-container">
      <div class="training-history__header-title-wrapper">
        <h4 class="dashboard__tab-sub-title training-history__header-title">
          Training History
        </h4>
        <div class="training-history__content-selector-dropdown">
          <Dropdown
            :options="contentTypesDropdown"
            :selectedOption="contentTypeSelectedDropdown"
            :handleSelection="selectContentType"
          ></Dropdown>
        </div>
        <div class="training-history__header-filter-wrapper">
          <div
            v-for="filter in filters"
            :id="`filter-${filter.name}`"
            :key="`f-${filter.name}`"
            class="training-history__header-checkbox-wrapper"
            @click="filterHandler(filter.id)"
          >
            <div class="ine-checkbox__input">
              <input
                v-model="filter.checked"
                class="ine-checkbox"
                type="checkbox"
                name="filter-checkbox"
              />
              <span class="ine-checkbox__control--small">
                <CheckboxTick />
              </span>
            </div>
            <span class="playlists__header-checkbox-label">{{
              filter.label
            }}</span>
          </div>
        </div>
      </div>
      <div class="training-history__content-selector-wrapper">
        <div
          v-for="contentType in contentTypes"
          :key="contentType.id"
          :class="[
            'training-history__content-selectable-item',
            {
              'training-history__content-selectable-item--selected':
                contentType.selected,
            },
          ]"
          @click="selectContentType(contentType)"
        >
          {{ contentType.name }}
        </div>
      </div>
    </div>

    <div class="training-history__items-container">
      <div v-if="loading" class="training-history__spinner-container">
        <ActionLoader
          class="training-history__items training-history--spinner"
        />
      </div>
      <div
        v-if="!isChangingContentType && contentItems && contentItems.length"
        class="training-history__items-wrapper"
      >
        <TrainingHistoryTile
          v-for="contentItem in contentItems"
          :key="contentItem.id"
          :contentItem="contentItem"
          :contentType="contentTypeSelected.id"
          :isFetching="loading"
          :windowWidth="windowWidth"
        />
        <TrainingHistoryPagination
          :items="contentItems"
          :totalItems="totalContentItems"
          :itemsPerPage="page_size"
          :currentPage="page"
          :isFetching="loading"
          @update-current-page-number="paginationHandler"
          @update-current-page-items="paginationHandler"
        />
      </div>
      <div v-else-if="!isChangingContentType" class="training-history__error">
        <h6 class="training-history__error-msg">None Available</h6>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CheckboxTick from '@/assets/images/certification/checkbox-tick.svg'
import TrainingHistoryTile from '@/components/user-dashboard/pass-analytics/TrainingHistoryTile.vue'
import TrainingHistoryPagination from '@/components/user-dashboard/pass-analytics/TrainingHistoryPagination.vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import Dropdown from '@/components/forms/elements/Dropdown.vue'
import { windowResize } from '@/helpers/mixins'
import { cloneDeep } from 'lodash'
import CONSTANTS from '@/constants'

const {
  COURSE,
  SKILL_DIVE,
  SKILL_CHECK,
  LEARNING_PATH,
} = CONSTANTS.ANALYTICS.HISTORY_CONTENT_TYPES

export default {
  name: 'TrainingHistory',
  components: {
    ActionLoader,
    Dropdown,
    TrainingHistoryTile,
    TrainingHistoryPagination,
    CheckboxTick: { ...CheckboxTick, name: 'CheckboxTick' },
  },
  mixins: [windowResize],
  props: {
    isLoading: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      isFetching: false,
      isChangingContentType: false,
      page_size: 10,
      page: 1,
      contentTypes: [
        { id: COURSE, name: 'Courses', selected: true },
        { id: LEARNING_PATH, name: 'Learning Paths', selected: false },
        { id: SKILL_DIVE, name: 'Skill Dive', selected: false },
        { id: SKILL_CHECK, name: 'Skill Check', selected: false },
      ],
      filters: [
        {
          id: 'in_progress',
          name: ['in_progress', 'started', 'partial'],
          label: 'In progress',
          checked: true,
        },
        {
          id: 'completed',
          name: ['completed', 'complete'],
          label: 'Completed',
          checked: true,
        },
      ],
      retryAtItems: null,
      previousState: {
        contentType: null,
        filters: null,
        pagination: {
          page: null,
          page_size: null,
        },
      },
    }
  },
  computed: {
    ...mapGetters({
      currentItems: 'content/getTrainingHistoryItems',
    }),
    loading() {
      return this.isFetching || this.isLoading
    },
    contentTypeSelected() {
      return this.contentTypes.find(ct => ct.selected)
    },
    contentTypeSelectedDropdown() {
      return {
        ...this.contentTypeSelected,
        label: this.contentTypeSelected.name,
      }
    },
    totalContentItems() {
      return this.currentItems?.count || 0
    },
    contentItems() {
      return this.currentItems?.results || []
    },
    currentFilterStatus() {
      const checkedFilters = this.filters.filter(f => f.checked)
      return checkedFilters.reduce((acc, filter) => {
        return acc.concat(filter.name)
      }, [])
    },
    contentTypesDropdown() {
      return this.contentTypes.map(c => {
        return { ...c, label: c.name }
      })
    },
  },
  created() {
    this.setupPreviousState()
  },
  methods: {
    ...mapActions('content', ['getTrainingHistory']),
    async fetchContent(payload = {}, changeContentType = false) {
      this.isFetching = true
      this.isChangingContentType = changeContentType || !this.currentItems

      const data = {
        page: this.page,
        page_size: this.page_size,
        status: this.currentFilterStatus,
        type: this.contentTypeSelected.id,
        ...payload,
      }

      this.setPageData(data)

      try {
        await this.getTrainingHistory(data)
      } catch (error) {
        this.restorePreviousState()
      } finally {
        this.isFetching = false
        this.isChangingContentType = false
      }
    },
    setPageData({ page, page_size }) {
      this.page = page || this.page
      this.page_size = page_size || this.page_size
    },
    restorePreviousState() {
      const { contentType, filters, pagination } = this.previousState

      this.setSelectedContentType({
        ...contentType,
        selected: true,
      })

      this.filters = filters
      this.page = pagination.page
      this.page_size = pagination.page_size
    },
    setupPreviousState() {
      this.previousState = {
        contentType: this.contentTypeSelected,
        filters: cloneDeep(this.filters),
        pagination: {
          page: this.page,
          page_size: this.page_size,
        },
      }
    },
    getProgress(value) {
      return Math.min(100, Math.floor(value * 100))
    },
    setSelectedContentType(contentType) {
      this.contentTypes = this.contentTypes.map(ct => ({
        ...ct,
        selected: ct.id === contentType.id,
      }))
    },
    selectContentType(contentType) {
      if (this.isFetching) return
      this.setupPreviousState()
      this.setSelectedContentType(contentType)
      const changeContentType = true
      this.fetchContent({ page: 1 }, changeContentType)
    },
    filterHandler(filterId) {
      if (this.isFetching) return
      this.setupPreviousState()
      const filter = this.filters.find(f => f.id === filterId)
      if (filter) filter.checked = !filter.checked
      this.fetchContent({ page: 1 })
    },
    paginationHandler(values) {
      if (this.isFetching) return
      this.setupPreviousState()
      this.fetchContent(values)
    },
  },
}
</script>

<style lang="scss" scoped>
.training-history {
  &__header {
    &-title {
      margin: 0;
      &-wrapper {
        display: flex;
        flex-direction: column;
        width: 100%;
        margin-bottom: 20px;
        gap: 20px;
        @include mobile {
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
        }
      }
    }
    &-filter-wrapper {
      display: flex;
      gap: 20px;
    }
    &-checkbox-wrapper {
      color: var(--white);
      font-size: rem(14);
      line-height: rem(20);
      display: flex;
      align-items: center;
      gap: 10px;
      cursor: pointer;
    }
  }
  &__content-selector-wrapper {
    display: none;
    @include mobile {
      display: flex;
      margin-bottom: 20px;
    }
  }
  &__content-selector-dropdown {
    display: block;
    @include mobile {
      display: none;
    }
  }
  &__content-selectable-item {
    color: var(--light-blue-gray);
    padding: 10px;
    text-align: center;
    font-size: rem(16);
    line-height: rem(23);
    cursor: pointer;
    width: 100%;
    height: 43px;
    border-bottom: 1px solid var(--light-blue-gray);
    &--selected {
      color: var(--white);
      font-weight: 700;
      border-bottom: 2px solid var(--white);
      transition: border-bottom 0.3s;
    }
  }
  &__items-container {
    display: flex;
    justify-content: center;
    width: 100%;
    position: relative;
    min-height: 500px;
  }
  &__items-wrapper {
    width: 100%;
  }
  &__error-msg {
    color: var(--white);
    text-align: center;
  }
  &__spinner-container {
    width: 105%;
    height: 100%;
    background: transparent;
    position: absolute;
    z-index: 50;
  }
  &--spinner {
    position: absolute;
    top: 40%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>
